import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type FocusEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SVGProps
} from "react";
import { createPortal } from "react-dom";

export type IconName =
  | "arrow-left" | "arrow-right" | "arrow-up-right" | "plus" | "search" | "settings" | "sun" | "moon"
  | "zoom-in" | "zoom-out" | "layers" | "bring-front" | "send-back" | "note" | "sticky-note"
  | "document" | "folder" | "folder-out" | "folder-minus" | "label" | "image" | "video" | "media" | "pdf" | "spreadsheet"
  | "browser" | "stack" | "trash" | "more" | "check" | "undo" | "redo" | "close" | "download"
  | "grid" | "fit-view" | "wifi" | "cloud" | "database" | "menu" | "sliders" | "copy" | "star"
  | "external-link" | "link" | "bold" | "italic" | "underline" | "paragraph" | "heading-one" | "heading-two" | "heading-three" | "heading-four" | "heading-five" | "list" | "list-ordered" | "list-checks"
  | "align-left" | "align-center" | "align-right" | "align-justify" | "quote" | "code" | "highlighter" | "arrow" | "frame" | "calendar" | "clock" | "lock" | "unlock";

const paths: Record<IconName, ReactNode> = {
  "arrow-left": <><path d="m14.5 5.5-6.5 6.5 6.5 6.5"/><path d="M8.5 12H20"/></>,
  "arrow-right": <><path d="m9.5 5.5 6.5 6.5-6.5 6.5"/><path d="M4 12h11.5"/></>,
  "arrow-up-right": <><path d="M8 7h9v9"/><path d="M17 7 7 17"/></>,
  plus: <path d="M12 5v14M5 12h14"/>,
  search: <><circle cx="10.75" cy="10.75" r="6.25"/><path d="m15.5 15.5 4 4"/></>,
  settings: <><circle cx="12" cy="12" r="2.75"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 8.97 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3v-4h.08A1.7 1.7 0 0 0 4.6 8.94a1.7 1.7 0 0 0-.34-1.88L4.2 7l2.83-2.83.06.06a1.7 1.7 0 0 0 1.88.34A1.7 1.7 0 0 0 10 3.04V3h4v.08a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06L19.8 7l-.06.06a1.7 1.7 0 0 0-.34 1.88A1.7 1.7 0 0 0 20.96 10H21v4h-.08A1.7 1.7 0 0 0 19.4 15Z"/></>,
  sun: <><circle cx="12" cy="12" r="3.5"/><path d="M12 2.75v2M12 19.25v2M5.46 5.46l1.42 1.42M17.12 17.12l1.42 1.42M2.75 12h2M19.25 12h2M5.46 18.54l1.42-1.42M17.12 6.88l1.42-1.42"/></>,
  moon: <path d="M20.25 15.35A8.5 8.5 0 0 1 8.65 3.75a8.5 8.5 0 1 0 11.6 11.6Z"/>,
  "zoom-in": <><circle cx="10.75" cy="10.75" r="6.25"/><path d="M10.75 7.75v6M7.75 10.75h6m1.75 4.75 4 4"/></>,
  "zoom-out": <><circle cx="10.75" cy="10.75" r="6.25"/><path d="M7.75 10.75h6m1.75 4.75 4 4"/></>,
  layers: <><path d="m12 3.5-8.5 4.75L12 13l8.5-4.75L12 3.5Z"/><path d="m3.5 12.25 8.5 4.75 8.5-4.75M3.5 16.25 12 21l8.5-4.75"/></>,
  "bring-front": <><rect x="7" y="3.5" width="13.5" height="13.5" rx="2.25"/><path d="M17 20.5H5.75A2.25 2.25 0 0 1 3.5 18.25V7"/><path d="m11 9 2-2 2 2M13 7v6"/></>,
  "send-back": <><rect x="3.5" y="7" width="13.5" height="13.5" rx="2.25"/><path d="M7 3.5h11.25a2.25 2.25 0 0 1 2.25 2.25V17"/><path d="m9 15 2 2 2-2M11 17v-6"/></>,
  note: <><rect x="5" y="3" width="14" height="18" rx="2.5"/><path d="M8.5 8h7M8.5 12h5"/></>,
  "sticky-note": <><path d="M6.5 3.5h8.25l3.75 3.75V18.5a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z"/><path d="M14.5 3.75V7.5h3.75"/></>,
  document: <><path d="M6.5 3.5h8.25l3.75 3.75V18.5a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z"/><path d="M14.5 3.75V7.5h3.75M8 12h7M8 15.5h5"/></>,
  folder: <path d="M3.5 7.25A2.25 2.25 0 0 1 5.75 5h4l2 2h6.5a2.25 2.25 0 0 1 2.25 2.25v7.5A2.25 2.25 0 0 1 18.25 19H5.75a2.25 2.25 0 0 1-2.25-2.25v-9.5Z"/>,
  "folder-out": <><path d="M3.5 7.25A2.25 2.25 0 0 1 5.75 5h4l2 2h6.5a2.25 2.25 0 0 1 2.25 2.25v7.5A2.25 2.25 0 0 1 18.25 19H5.75a2.25 2.25 0 0 1-2.25-2.25v-9.5Z"/><path d="m10 11-3 3 3 3M7 14h8"/></>,
  "folder-minus": <><path d="M3.5 7.25A2.25 2.25 0 0 1 5.75 5h4l2 2h6.5a2.25 2.25 0 0 1 2.25 2.25v7.5A2.25 2.25 0 0 1 18.25 19H5.75a2.25 2.25 0 0 1-2.25-2.25v-9.5Z"/><path d="M8.5 13.5h7"/></>,
  label: <><path d="M4.25 5.5h8.5l7 7-7 7-8.5-8.5V5.5Z"/><circle cx="8.5" cy="9.75" r="1"/></>,
  image: <><rect x="3.5" y="4.5" width="17" height="15" rx="2.5"/><circle cx="9" cy="9.5" r="1.5"/><path d="m4 17 4.5-4.5 3.5 3.25 2.5-2.5 5.5 5"/></>,
  video: <><rect x="3.5" y="5" width="17" height="14" rx="2.5"/><path d="m10 9 5 3-5 3V9Z"/></>,
  media: <><rect x="3.5" y="6.5" width="14" height="13" rx="2.25"/><path d="M7 6.5V5.75A2.25 2.25 0 0 1 9.25 3.5h9A2.25 2.25 0 0 1 20.5 5.75v8A2.25 2.25 0 0 1 18.25 16h-.75"/><circle cx="8.5" cy="11" r="1.25"/><path d="m4 17 3.75-3.75L13.5 19"/></>,
  pdf: <><path d="M6.5 3.5h8.25l3.75 3.75V18.5a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z"/><path d="M14.5 3.75V7.5h3.75M8 12h8M8 15.5h5"/><path d="M9 18h6"/></>,
  spreadsheet: <><path d="M6.5 3.5h8.25l3.75 3.75V18.5a2 2 0 0 1-2 2h-10a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z"/><path d="M14.5 3.75V7.5h3.75M8 11h8v6H8zM8 14h8M11 11v6"/></>,
  browser: <><rect x="3.5" y="4.5" width="17" height="15" rx="2.5"/><path d="M3.75 8.5h16.5"/><circle cx="7" cy="6.5" r=".5" fill="currentColor" stroke="none"/><circle cx="9.5" cy="6.5" r=".5" fill="currentColor" stroke="none"/></>,
  stack: <><rect x="5.5" y="4.5" width="14" height="14" rx="2.5"/><path d="M8.5 2h8M8.5 21h8"/></>,
  trash: <><path d="M4.5 7h15M9.25 7V4.5h5.5V7M6.75 7l.85 12.25a1.5 1.5 0 0 0 1.5 1.4h5.8a1.5 1.5 0 0 0 1.5-1.4L17.25 7"/><path d="M10 11v5.5M14 11v5.5"/></>,
  more: <><circle cx="5" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1" fill="currentColor" stroke="none"/></>,
  check: <path d="m5 12.5 4.25 4L19 6.75"/>,
  undo: <><path d="m8.5 7-4.5 4.5L8.5 16"/><path d="M4.5 11.5h8.25A6.25 6.25 0 0 1 19 17.75"/></>,
  redo: <><path d="m15.5 7 4.5 4.5-4.5 4.5"/><path d="M19.5 11.5h-8.25A6.25 6.25 0 0 0 5 17.75"/></>,
  close: <path d="m6.5 6.5 11 11M17.5 6.5l-11 11"/>,
  download: <><path d="M12 3.5v11M7.75 10.5 12 14.75l4.25-4.25"/><path d="M5 20.5h14"/></>,
  grid: <><rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/></>,
  "fit-view": <><path d="M9 3.5H5.5a2 2 0 0 0-2 2V9M15 3.5h3.5a2 2 0 0 1 2 2V9M9 20.5H5.5a2 2 0 0 1-2-2V15M15 20.5h3.5a2 2 0 0 0 2-2V15"/><rect x="7" y="8" width="4" height="4" rx="1"/><rect x="13" y="11" width="4" height="5" rx="1"/></>,
  wifi: <><path d="M4.75 10.5a10.25 10.25 0 0 1 14.5 0M7.75 14a6 6 0 0 1 8.5 0M10.75 17.5a1.75 1.75 0 0 1 2.5 0"/><circle cx="12" cy="19.5" r=".75" fill="currentColor" stroke="none"/></>,
  cloud: <path d="M7 18.5h10.5a3.75 3.75 0 0 0 .55-7.45A6.25 6.25 0 0 0 6.15 9.3 4.65 4.65 0 0 0 7 18.5Z"/>,
  database: <><ellipse cx="12" cy="5.5" rx="7.5" ry="3"/><path d="M4.5 5.5v6c0 1.65 3.35 3 7.5 3s7.5-1.35 7.5-3v-6M4.5 11.5v6c0 1.65 3.35 3 7.5 3s7.5-1.35 7.5-3v-6"/></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16"/>,
  sliders: <><path d="M4 7h5M15 7h5M4 17h9M19 17h1"/><circle cx="12" cy="7" r="3"/><circle cx="16" cy="17" r="3"/></>,
  copy: <><rect x="8" y="8" width="11.5" height="11.5" rx="2.25"/><path d="M16 8V6.75A2.25 2.25 0 0 0 13.75 4.5h-7A2.25 2.25 0 0 0 4.5 6.75v7A2.25 2.25 0 0 0 6.75 16H8"/></>,
  star: <path d="m12 3.75 2.48 5.03 5.55.8-4.01 3.92.95 5.52L12 16.41l-4.97 2.61.95-5.52-4.01-3.92 5.55-.8L12 3.75Z"/>,
  "external-link": <><path d="M13.5 4.5h6v6M19.5 4.5l-9 9"/><path d="M18.5 13v5.25a1.25 1.25 0 0 1-1.25 1.25H5.75a1.25 1.25 0 0 1-1.25-1.25V6.75A1.25 1.25 0 0 1 5.75 5.5H11"/></>,
  link: <><path d="M10 14a4.25 4.25 0 0 0 6 0l3.5-3.5a4.25 4.25 0 0 0-6-6l-1 1"/><path d="M14 10a4.25 4.25 0 0 0-6 0l-3.5 3.5a4.25 4.25 0 0 0 6 6l1-1"/></>,
  bold: <><path d="M7.5 4.5h5.25a4 4 0 0 1 0 8H7.5v-8Z"/><path d="M7.5 12.5h6a3.5 3.5 0 0 1 0 7h-6v-7Z"/></>,
  italic: <><path d="M10 4.5h7M7 19.5h7M14 4.5 10 19.5"/></>,
  underline: <><path d="M7 4.5v7.25a5 5 0 0 0 10 0V4.5"/><path d="M5 20h14"/></>,
  paragraph: <><path d="M13 5v14M17 5v14"/><path d="M13 5H9.5a4 4 0 0 0 0 8H13"/></>,
  "heading-one": <><path d="M4.5 5v14M13 5v14M4.5 12h8.5"/><path d="M18.5 14.5v6M16.5 16.5l2-2"/></>,
  "heading-two": <><path d="M4.5 5v14M13 5v14M4.5 12h8.5"/><path d="M16 14.5a2.5 2.5 0 1 1 4.5 1.5L16 20.5h5"/></>,
  "heading-three": <><path d="M4.5 5v14M13 5v14M4.5 12h8.5"/><path d="M16.5 15a2.5 2.5 0 1 1 2 4.1M18.5 19.1a2.5 2.5 0 1 1-2 1"/></>,
  "heading-four": <><path d="M4.5 5v14M13 5v14M4.5 12h8.5"/><path d="M20 20.5v-6l-4 4h5"/></>,
  "heading-five": <><path d="M4.5 5v14M13 5v14M4.5 12h8.5"/><path d="M20.5 14.5H16l-.25 2.5h2.5a1.75 1.75 0 1 1 0 3.5H16"/></>,
  "align-left": <><path d="M4 6h16M4 10h11M4 14h16M4 18h9"/></>,
  "align-center": <><path d="M4 6h16M6.5 10h11M4 14h16M7.5 18h9"/></>,
  "align-right": <><path d="M4 6h16M9 10h11M4 14h16M11 18h9"/></>,
  "align-justify": <><path d="M4 6h16M4 10h16M4 14h16M4 18h16"/></>,
  list: <><path d="M9 6h11M9 12h11M9 18h11"/><circle cx="4.5" cy="6" r=".75" fill="currentColor" stroke="none"/><circle cx="4.5" cy="12" r=".75" fill="currentColor" stroke="none"/><circle cx="4.5" cy="18" r=".75" fill="currentColor" stroke="none"/></>,
  "list-ordered": <><path d="M10 6h10M10 12h10M10 18h10"/><path d="M4 5h1v3M4 8h2M4 13a1.5 1.5 0 1 1 2.5 1L4 17h3"/></>,
  "list-checks": <><path d="m3.5 6 1.5 1.5L7.5 5M10 6h10M3.5 12l1.5 1.5 2.5-2.5M10 12h10M3.5 18l1.5 1.5 2.5-2.5M10 18h10"/></>,
  quote: <><path d="M9.5 9H5.75A1.75 1.75 0 0 0 4 10.75v2.5A1.75 1.75 0 0 0 5.75 15H8v1a3 3 0 0 1-3 3"/><path d="M20 9h-3.75a1.75 1.75 0 0 0-1.75 1.75v2.5A1.75 1.75 0 0 0 16.25 15h2.25v1a3 3 0 0 1-3 3"/></>,
  code: <><path d="m8.5 7-5 5 5 5M15.5 7l5 5-5 5M13.5 4.5l-3 15"/></>,
  highlighter: <><path d="m14.5 4.5 5 5-9.25 9.25H5.5v-4.5L14.5 4.5Z"/><path d="m12.25 7 5 5M4 21h16"/></>,
  arrow: <><path d="M4 17.5C8.5 17.5 11 7 19.5 7"/><path d="m15.5 3 4 4-4 4"/></>,
  frame: <><path d="M8 4H5.5A1.5 1.5 0 0 0 4 5.5V8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16M16 20h2.5a1.5 1.5 0 0 0 1.5-1.5V16"/><path d="M8 8h8v8H8z"/></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15.5" rx="2.5"/><path d="M7.5 3v4M16.5 3v4M3.5 9.5h17"/><path d="M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01"/></>,
  clock: <><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3.25 2"/></>,
  lock: <><rect x="5" y="10.5" width="14" height="10" rx="2.25"/><path d="M8.25 10.5V7.75a3.75 3.75 0 0 1 7.5 0v2.75"/><path d="M12 14.5v2"/></>,
  unlock: <><rect x="5" y="10.5" width="14" height="10" rx="2.25"/><path d="M8.25 10.5V7.75a3.75 3.75 0 0 1 7.2-1.45"/><path d="M12 14.5v2"/></>
};

export function Icon({ name, className = "", ...props }: { name: IconName } & SVGProps<SVGSVGElement>) {
  return <svg aria-hidden="true" data-icon={name} className={`br-icon ${className}`.trim()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...props}>{paths[name]}</svg>;
}

export type TooltipPlacement = "top" | "right" | "bottom" | "left";

interface TooltipTriggerProps {
  ref?: Ref<HTMLElement>;
  "aria-describedby"?: string;
  onBlur?: FocusEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLElement>;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  onPointerCancel?: PointerEventHandler<HTMLElement>;
  onPointerDown?: PointerEventHandler<HTMLElement>;
  onPointerEnter?: PointerEventHandler<HTMLElement>;
  onPointerLeave?: PointerEventHandler<HTMLElement>;
  onPointerUp?: PointerEventHandler<HTMLElement>;
}

interface TooltipPosition {
  top: number;
  left: number;
  placement: TooltipPlacement;
  arrowOffset: number;
}

const TOOLTIP_GAP = 9;
const TOOLTIP_VIEWPORT_PADDING = 10;

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function isElementVisible(element: HTMLElement): boolean {
  if (!element.isConnected) return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = getComputedStyle(current);
    if (current.hidden || style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
  }
  return true;
}

export function Tooltip({ label, shortcut, placement = "top", delay = 420, children }: {
  label: string;
  shortcut?: string | undefined;
  placement?: TooltipPlacement;
  delay?: number;
  children: ReactElement<TooltipTriggerProps>;
}) {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pointerResetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pointerDown = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const childRef = children.props.ref;
  const setAnchorRef = useCallback((node: HTMLElement | null) => {
    anchorRef.current = node;
    assignRef(childRef, node);
  }, [childRef]);

  const hide = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = undefined;
    setOpen(false);
  }, []);

  const show = useCallback((wait: number) => {
    if (openTimer.current) clearTimeout(openTimer.current);
    setPosition(null);
    openTimer.current = setTimeout(() => {
      openTimer.current = undefined;
      const anchor = anchorRef.current;
      if (anchor && isElementVisible(anchor)) setOpen(true);
    }, wait);
  }, []);

  useEffect(() => () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;
      if (!isElementVisible(anchor)) {
        hide();
        return;
      }
      const anchorBounds = anchor.getBoundingClientRect();
      const tooltipBounds = tooltip.getBoundingClientRect();
      const room = {
        top: anchorBounds.top - TOOLTIP_VIEWPORT_PADDING,
        right: window.innerWidth - anchorBounds.right - TOOLTIP_VIEWPORT_PADDING,
        bottom: window.innerHeight - anchorBounds.bottom - TOOLTIP_VIEWPORT_PADDING,
        left: anchorBounds.left - TOOLTIP_VIEWPORT_PADDING
      };
      const opposite: Record<TooltipPlacement, TooltipPlacement> = { top: "bottom", right: "left", bottom: "top", left: "right" };
      const required = placement === "top" || placement === "bottom" ? tooltipBounds.height : tooltipBounds.width;
      let resolved = placement;
      if (room[placement] < required + TOOLTIP_GAP && room[opposite[placement]] > room[placement]) resolved = opposite[placement];

      if (resolved === "top" || resolved === "bottom") {
        const anchorCenter = anchorBounds.left + anchorBounds.width / 2;
        const left = clamp(anchorCenter - tooltipBounds.width / 2, TOOLTIP_VIEWPORT_PADDING, window.innerWidth - tooltipBounds.width - TOOLTIP_VIEWPORT_PADDING);
        const top = resolved === "top" ? anchorBounds.top - tooltipBounds.height - TOOLTIP_GAP : anchorBounds.bottom + TOOLTIP_GAP;
        setPosition({ top, left, placement: resolved, arrowOffset: clamp(anchorCenter - left, 12, tooltipBounds.width - 12) });
        return;
      }

      const anchorCenter = anchorBounds.top + anchorBounds.height / 2;
      const top = clamp(anchorCenter - tooltipBounds.height / 2, TOOLTIP_VIEWPORT_PADDING, window.innerHeight - tooltipBounds.height - TOOLTIP_VIEWPORT_PADDING);
      const left = resolved === "left" ? anchorBounds.left - tooltipBounds.width - TOOLTIP_GAP : anchorBounds.right + TOOLTIP_GAP;
      setPosition({ top, left, placement: resolved, arrowOffset: clamp(anchorCenter - top, 12, tooltipBounds.height - 12) });
    };

    updatePosition();
    const visibilityObserver = typeof MutationObserver === "undefined" ? null : new MutationObserver(updatePosition);
    for (let current: HTMLElement | null = anchorRef.current; current; current = current.parentElement) {
      visibilityObserver?.observe(current, { attributes: true, attributeFilter: ["class", "hidden", "style"] });
    }
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      visibilityObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [hide, open, placement]);

  const describedBy = open ? [children.props["aria-describedby"], id].filter(Boolean).join(" ") : children.props["aria-describedby"];
  const trigger = cloneElement(children, {
    ref: setAnchorRef,
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    onPointerEnter: (event) => {
      children.props.onPointerEnter?.(event);
      if (!event.defaultPrevented && event.pointerType !== "touch") show(delay);
    },
    onPointerLeave: (event) => {
      children.props.onPointerLeave?.(event);
      if (!event.defaultPrevented) hide();
    },
    onPointerDown: (event) => {
      children.props.onPointerDown?.(event);
      if (!event.defaultPrevented) {
        pointerDown.current = true;
        if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
        pointerResetTimer.current = setTimeout(() => {
          pointerDown.current = false;
          pointerResetTimer.current = undefined;
        }, 0);
        hide();
      }
    },
    onPointerUp: (event) => {
      children.props.onPointerUp?.(event);
      pointerDown.current = false;
      if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
      pointerResetTimer.current = undefined;
    },
    onPointerCancel: (event) => {
      children.props.onPointerCancel?.(event);
      pointerDown.current = false;
      if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
      pointerResetTimer.current = undefined;
    },
    onFocus: (event) => {
      children.props.onFocus?.(event);
      if (!event.defaultPrevented && !pointerDown.current) show(0);
    },
    onBlur: (event) => {
      children.props.onBlur?.(event);
      if (!event.defaultPrevented) hide();
    },
    onKeyDown: (event) => {
      children.props.onKeyDown?.(event);
      if (!event.defaultPrevented && event.key === "Escape") hide();
    }
  });

  const tooltip = open && typeof document !== "undefined" ? createPortal(
    <div
      ref={tooltipRef}
      id={id}
      role="tooltip"
      className="br-tooltip"
      data-placement={position?.placement ?? placement}
      data-positioned={position ? "true" : "false"}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        "--br-tooltip-arrow-offset": `${position?.arrowOffset ?? 16}px`
      } as CSSProperties}
    >
      <span className="br-tooltip__label">{label}</span>
      {shortcut && <kbd>{shortcut}</kbd>}
    </div>,
    document.body
  ) : null;

  return <>{trigger}{tooltip}</>;
}

export function IconButton({ label, icon, className = "", tooltip, tooltipShortcut, tooltipPlacement, title, ...props }: {
  label: string;
  icon: IconName;
  tooltip?: string | false | undefined;
  tooltipShortcut?: string | undefined;
  tooltipPlacement?: TooltipPlacement | undefined;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const button = <button className={`br-icon-button ${className}`} aria-label={label} {...props}><Icon name={icon}/></button>;
  const tooltipLabel = tooltip === false ? null : tooltip ?? title ?? label;
  return tooltipLabel ? <Tooltip label={tooltipLabel} shortcut={tooltipShortcut} placement={tooltipPlacement ?? "top"}>{button}</Tooltip> : button;
}

export function Button({ children, className = "", tone = "default", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: "default" | "primary" | "danger" }) {
  return <button className={`br-button br-button--${tone} ${className}`} {...props}>{children}</button>;
}

export function FloatingPanel({ children, className = "", ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`br-floating-panel ${className}`} {...props}>{children}</div>;
}

export function StrataMark({ size = 36, className = "" }: { size?: number; className?: string }) {
  return <span className={`br-strata-mark ${className}`} style={{ width: size, height: size }} aria-label="Bedrock">
    <span/><span/><span/>
  </span>;
}

export function Modal({ title, children, onClose, wide = false, immersive = false, className = "", overlayLayer }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean; immersive?: boolean; className?: string; overlayLayer?: number }) {
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.defaultPrevented) return;
      if ((event.target as HTMLElement | null)?.closest?.("[data-modal-escape-boundary='true']")) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape, { capture: true });
    return () => window.removeEventListener("keydown", closeOnEscape, { capture: true });
  }, [onClose]);

  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const syncBackdropToVisualViewport = () => {
      const backdrop = backdropRef.current;
      if (!backdrop) return;
      backdrop.style.setProperty("--br-visual-viewport-top", `${viewport.offsetTop}px`);
      backdrop.style.setProperty("--br-visual-viewport-left", `${viewport.offsetLeft}px`);
      backdrop.style.setProperty("--br-visual-viewport-width", `${viewport.width}px`);
      backdrop.style.setProperty("--br-visual-viewport-height", `${viewport.height}px`);
    };

    syncBackdropToVisualViewport();
    viewport.addEventListener("resize", syncBackdropToVisualViewport);
    viewport.addEventListener("scroll", syncBackdropToVisualViewport);
    return () => {
      viewport.removeEventListener("resize", syncBackdropToVisualViewport);
      viewport.removeEventListener("scroll", syncBackdropToVisualViewport);
    };
  }, []);

  return <div ref={backdropRef} className={`br-modal-backdrop ${immersive ? "br-modal-backdrop--immersive" : ""}`} role="presentation" style={overlayLayer === undefined ? undefined : { zIndex: overlayLayer }} onMouseDown={onClose}>
    <section className={`br-modal ${wide ? "br-modal--wide" : ""} ${immersive ? "br-modal--immersive" : ""} ${className}`} role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
      {immersive ? <header><IconButton label="Back" icon="arrow-left" onClick={onClose}/><h2>{title}</h2></header> : <header><h2>{title}</h2><IconButton label="Close" icon="close" onClick={onClose}/></header>}
      <div className="br-modal__body">{children}</div>
    </section>
  </div>;
}
